import React, { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

/** Emails a fresh code, then hands a single-use grant to `onConfirm(grant)`. */
export function StepUpDialog({ request, onClose }) {
  const [token, setToken] = useState("");
  const [hint, setHint] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!request) {
      return;
    }
    setToken("");
    setCode("");
    api.post("/auth/step-up/request", { purpose: request.purpose })
      .then(({ data }) => { setToken(data.step_up_token); setHint(data.email_hint); })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [request, onClose]);

  const confirm = async (event) => {
    event.preventDefault();
    setBusy(true);
    let data;
    try {
      ({ data } = await api.post("/auth/step-up/verify", { step_up_token: token, code }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
      return;
    }
    try {
      await request.onConfirm(data.grant);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <Dialog open={Boolean(request)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent data-testid="step-up-dialog">
        <form onSubmit={confirm}>
          <DialogHeader>
            <DialogTitle>{request?.title}</DialogTitle>
            <DialogDescription>
              {token ? `Enter the 6-digit code we emailed to ${hint}. It expires in 10 minutes.` : "Sending a verification code…"}
            </DialogDescription>
          </DialogHeader>
          <input
            className="input-flat font-mono tracking-[0.4em] my-5" inputMode="numeric" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            disabled={!token} autoFocus data-testid="step-up-dialog-code"
          />
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={onClose} data-testid="step-up-dialog-cancel">Cancel</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={busy || code.length !== 6} data-testid="step-up-dialog-confirm">
              <ShieldCheck size={14} /> {busy ? "Confirming…" : "Confirm"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
